import { toast } from 'sonner';
import {
  Camera, CheckCircle2, AlertTriangle, ArrowRight,
} from 'lucide-react';
import {
  C, STAGES, DEMO_AI_FEED, filledSlots, todayStr,
  noShowRiskCount, avgWait, copaysCollected, copaysOutstanding,
  checkedInCount,
} from './frontDeskData';

export const FDSidebar = ({
  sidebarW, activeStage, setActiveStage, stageCounts,
  queuePatients, activePatientId, view, selectPatient, navigate,
}) => (
  <div style={{ width: sidebarW, background: '#fff', borderRight: '.5px solid #ebebeb', display: 'flex', flexDirection: 'column', flexShrink: 0 }} data-testid="fd-sidebar">
    {/* Block 1: Mode + date */}
    <div style={{ padding: '8px 12px', borderBottom: '.5px solid #f0f0f0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#f5f5f5', border: '.5px solid #ebebeb', borderRadius: 4, padding: '2px 7px', width: 'fit-content' }}>
        <span style={{ width: 5, height: 5, borderRadius: '50%', background: C.green }} />
        <span style={{ fontSize: 10, color: '#555', fontWeight: 500 }}>Front Desk mode</span>
      </div>
      <p style={{ fontSize: 9, color: '#aaa', marginTop: 3 }}>{todayStr} &middot; {filledSlots} visits today</p>
    </div>

    {/* Block 2: Lens */}
    <div style={{ padding: '8px 10px', borderBottom: '.5px solid #f0f0f0' }}>
      <button onClick={() => toast.info('Lens camera opening', { description: 'Scan ID or insurance card' })}
        style={{ width: '100%', height: 30, borderRadius: 7, background: C.black, color: '#fff', fontSize: 10, fontWeight: 500, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}
        data-testid="fd-scan-lens-btn">
        <Camera style={{ width: 10, height: 10 }} /> Scan ID or insurance card
      </button>
      <p style={{ fontSize: 9, color: '#bbb', textAlign: 'center', marginTop: 3 }}>Tap to add new patient or verify insurance</p>
    </div>

    {/* Block 3: Color key */}
    <div style={{ padding: '6px 10px', borderBottom: '.5px solid #f0f0f0' }}>
      <p style={{ fontSize: 9, color: '#bbb', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>Color key</p>
      {[
        { shape: 'circle', color: C.green, label: 'Confirmed / checked in' },
        { shape: 'circle', color: C.amber, label: 'Waiting or attention needed' },
        { shape: 'circle', color: C.red, label: 'No-show risk / blocked' },
        { shape: 'circle', color: '#d1d5db', label: 'Done / checked out' },
        { shape: 'square', color: C.purple, label: 'Active patient' },
      ].map((c, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2, fontSize: 10, color: '#666' }}>
          <div style={{ width: 7, height: 7, borderRadius: c.shape === 'circle' ? '50%' : 1.5, background: c.color, flexShrink: 0 }} />
          {c.label}
        </div>
      ))}
    </div>

    {/* Block 4: Pipeline */}
    <div style={{ borderBottom: '.5px solid #f0f0f0', padding: '4px 0' }}>
      <p style={{ fontSize: 9, color: '#bbb', textTransform: 'uppercase', letterSpacing: '.05em', padding: '0 12px', marginBottom: 2 }}>Patient flow</p>
      {STAGES.map((stg) => {
        const isActive = activeStage === stg.key;
        const Icon = stg.icon;
        const count = stageCounts[stg.key] || 0;
        const indicator = stg.key === 'scheduled' ? (noShowRiskCount > 0 ? `${noShowRiskCount} at risk` : '') :
          stg.key === 'arrived' ? (avgWait > 0 ? `~${avgWait}m avg wait` : '') :
          stg.key === 'with_provider' ? `${count} rooms in use` :
          `$${copaysCollected} copays done`;
        const indColor = stg.key === 'scheduled' ? (noShowRiskCount > 0 ? C.amber : '#aaa') :
          stg.key === 'arrived' ? (avgWait > 15 ? C.amber : C.green) :
          stg.key === 'with_provider' ? '#aaa' : C.green;
        return (
          <button key={stg.key} onClick={() => setActiveStage(stg.key)}
            style={{ display: 'flex', alignItems: 'center', gap: 7, width: '100%', padding: '5px 12px', background: isActive ? '#f9f9f9' : 'transparent', cursor: 'pointer', textAlign: 'left', border: 'none', borderLeft: `2.5px solid ${isActive ? C.purple : 'transparent'}` }}
            data-testid={`fd-pipeline-${stg.key}`}>
            <div style={{ width: 18, height: 18, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: stg.key === 'checked_out' ? C.black : isActive ? C.purple : '#f0f0f0', color: isActive || stg.key === 'checked_out' ? '#fff' : '#aaa', fontSize: 9, fontWeight: 600, border: !isActive && stg.key !== 'checked_out' ? '.5px solid #e0e0e0' : 'none' }}>
              {stg.key === 'checked_out' ? <CheckCircle2 style={{ width: 10, height: 10 }} /> : <Icon style={{ width: 10, height: 10 }} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: 11, fontWeight: isActive ? 500 : 400, color: isActive ? C.black : '#bbb', display: 'block' }}>{stg.label}</span>
              <span style={{ fontSize: 9, color: '#aaa' }}>{count} patient{count !== 1 ? 's' : ''}</span>
            </div>
            {indicator && <span style={{ fontSize: 9, color: indColor, whiteSpace: 'nowrap' }}>{indicator}</span>}
          </button>
        );
      })}
    </div>

    {/* Block 5: Patient queue */}
    <div style={{ flex: 1, overflowY: 'auto', padding: '2px 0' }}>
      <p style={{ fontSize: 9, color: '#bbb', textTransform: 'uppercase', letterSpacing: '.05em', padding: '4px 12px 2px' }}>
        {STAGES.find(s => s.key === activeStage)?.label} — {queuePatients.length} patient{queuePatients.length !== 1 ? 's' : ''}
      </p>
      {queuePatients.length === 0 ? (
        <p style={{ fontSize: 10, color: '#bbb', textAlign: 'center', padding: 16 }}>No patients in this stage</p>
      ) : queuePatients.map(p => {
        const isActive = p.id === activePatientId && view === 'patient';
        const dotColor = p.stage === 'checked_out' ? '#d1d5db' : p.noShowRisk >= 25 ? C.red : p.aiStatus.type === 'insurance_flagged' ? C.red : p.aiStatus.type === 'no_show_risk' ? C.amber : C.green;
        const badge = p.stage === 'checked_out' ? { bg: '#f0f0f0', color: '#aaa', text: 'Done' } :
          p.noShowRisk >= 25 ? { bg: '#fef2f2', color: '#A32D2D', text: 'At risk' } :
          p.aiStatus.type === 'insurance_flagged' ? { bg: '#fef2f2', color: '#A32D2D', text: 'Blocked' } :
          p.copay.amount > 0 && !p.copay.collected ? { bg: '#fef3c7', color: '#854F0B', text: `$${p.copay.amount} due` } :
          p.stage === 'arrived' ? { bg: '#f0f0f0', color: '#555', text: 'Checked in' } :
          { bg: '#f0f0f0', color: '#555', text: p.stage === 'with_provider' ? 'In room' : 'Confirmed' };
        return (
          <button key={p.id} onClick={() => selectPatient(p.id)}
            style={{ display: 'flex', alignItems: 'flex-start', gap: 6, width: '100%', padding: '6px 12px', background: isActive ? '#f9f9f9' : 'transparent', cursor: 'pointer', textAlign: 'left', border: 'none', borderLeft: `2.5px solid ${isActive ? C.purple : 'transparent'}`, opacity: p.stage === 'checked_out' ? 0.45 : 1 }}
            data-testid={`fd-patient-row-${p.id}`}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: isActive ? C.purple : dotColor, flexShrink: 0, marginTop: 3 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: 11, fontWeight: 500, color: C.black, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
              <span style={{ fontSize: 9, color: '#aaa' }}>{p.time} &middot; {p.type}</span>
              {/* AI STATUS LINE — always shown */}
              <span style={{ fontSize: 10, color: p.aiStatus.color, display: 'block', marginTop: 1 }}>{p.aiStatus.type === 'confirmed' ? '\u2713' : p.aiStatus.type === 'no_show_risk' ? '\u26a0' : p.aiStatus.type === 'insurance_flagged' ? '\u2715' : '\u2192'} {p.aiStatus.text}</span>
            </div>
            <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, background: badge.bg, color: badge.color, fontWeight: 500, flexShrink: 0, marginTop: 1 }}>{badge.text}</span>
          </button>
        );
      })}
    </div>

    {/* Block 6: Echo AI status strip */}
    <div style={{ margin: '0 10px 6px', background: '#f9f9f9', border: '.5px solid #ebebeb', borderRadius: 7, padding: '7px 9px', cursor: 'pointer' }}
      onClick={() => toast.info('Opening Comms Hub')} data-testid="fd-ai-strip">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 500, color: C.black }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.green, boxShadow: `0 0 4px ${C.green}`, animation: 'pulse 2s infinite' }} />Echo AI &middot; Active
        </span>
        <span style={{ fontSize: 9, color: '#aaa' }}>just now</span>
      </div>
      {DEMO_AI_FEED.slice(0, 2).map((a, i) => (
        <p key={i} style={{ fontSize: 9, color: '#555', margin: '2px 0' }}>{a.icon} {a.text} &middot; {a.time}</p>
      ))}
      <p style={{ fontSize: 9, color: '#aaa', marginTop: 4 }}>7 calls &middot; 5 reminders &middot; 3 verifications today</p>
    </div>

    {/* Sidebar footer */}
    <div style={{ padding: '6px 12px', borderTop: '.5px solid #f0f0f0', fontSize: 10 }}>
      <div style={{ display: 'flex', gap: 6, color: '#aaa', marginBottom: 3 }}>
        <span><strong style={{ color: '#555' }}>{checkedInCount}</strong> checked in</span>&middot;
        <span><strong style={{ color: '#555' }}>{stageCounts.with_provider || 0}</strong> in rooms</span>&middot;
        <span><strong style={{ color: '#555' }}>{stageCounts.checked_out || 0}</strong> done</span>
      </div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
        <span style={{ color: C.green }}>${copaysCollected} collected</span>&middot;
        <span style={{ color: copaysOutstanding > 0 ? C.amber : '#aaa' }}>${copaysOutstanding} outstanding</span>
      </div>
      <button onClick={() => navigate('/revq/billing-focus')}
        style={{ width: '100%', height: 24, borderRadius: 5, background: C.black, color: '#fff', fontSize: 9, fontWeight: 500, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}
        data-testid="fd-revq-link">Submit all clean (RevQ) <ArrowRight style={{ width: 9, height: 9 }} /></button>
    </div>
  </div>
);
